import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { ActivateAccountPage } from '@/features/auth/pages/ActivateAccountPage';
import { ConsultaPage } from '@/features/credencial-publica/pages/ConsultaPage';
import { VerCredencialPage } from '@/features/credencial-publica/pages/VerCredencialPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PageFallbackSkeleton } from '@/components/ui/skeleton';

/**
 * Top-level del panel admin (refactor v2).
 *
 * Estructura del menú lateral (`AppShell`):
 *   - Inicio         `/`
 *   - Gestión Académica  `/gestion-academica` y sub-rutas
 *   - Auditoría      `/auditoria`
 *   - Configuración  `/configuracion` y sub-rutas (General / Usuarios / Permisos y grupos)
 *
 * Las páginas "huérfanas" del modelo plano anterior
 * (`/programas`, `/cohortes`, `/participantes`, `/sesiones`,
 *  `/justificaciones`, `/reportes`) ya no son accesibles — Fase 4 las
 * reescribe como tabs dentro del sub-árbol jerárquico.
 *
 * El programa-detail y cohorte-detail se exponen en /gestion-academica/
 * para mantener la jerarquía explícita en la URL.
 */

// Code splitting: cada página top-level va en su propio chunk.
const InicioPage = lazy(() => import('@/pages/InicioPage').then((m) => ({ default: m.InicioPage })));
const GestionAcademicaPage = lazy(() =>
  import('@/pages/gestion-academica/GestionAcademicaPage').then((m) => ({ default: m.GestionAcademicaPage })),
);
const ProgramaDetallePage = lazy(() =>
  import('@/pages/gestion-academica/ProgramaDetallePage').then((m) => ({ default: m.ProgramaDetallePage })),
);
const UnidadesCurricularesPage = lazy(() =>
  import('@/pages/gestion-academica/UnidadesCurricularesPage').then((m) => ({ default: m.UnidadesCurricularesPage })),
);
const CohorteDetallePage = lazy(() =>
  import('@/pages/gestion-academica/CohorteDetallePage').then((m) => ({ default: m.CohorteDetallePage })),
);
const AsistenciaListaPage = lazy(() =>
  import('@/pages/gestion-academica/AsistenciaListaPage').then((m) => ({ default: m.AsistenciaListaPage })),
);
const AuditoriaPage = lazy(() => import('@/pages/AuditoriaPage').then((m) => ({ default: m.AuditoriaPage })));

// Configuración: layout con sub-rutas
const ConfiguracionLayoutPage = lazy(() =>
  import('@/pages/configuracion/ConfiguracionLayoutPage').then((m) => ({ default: m.ConfiguracionLayoutPage })),
);
const ConfiguracionGeneralPage = lazy(() =>
  import('@/pages/configuracion/ConfiguracionGeneralPage').then((m) => ({ default: m.ConfiguracionGeneralPage })),
);
const UsuariosPage = lazy(() =>
  import('@/pages/configuracion/UsuariosPage').then((m) => ({ default: m.UsuariosPage })),
);
const PermisosPage = lazy(() =>
  import('@/pages/configuracion/PermisosPage').then((m) => ({ default: m.PermisosPage })),
);

function lazyEl(Comp: React.LazyExoticComponent<React.ComponentType>) {
  return (
    <Suspense fallback={<PageFallbackSkeleton />}>
      <Comp />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/activate', element: <ActivateAccountPage /> },
  {
    path: '/publica',
    children: [
      { index: true, element: <ConsultaPage /> },
      { path: 'ver', element: <VerCredencialPage /> },
    ],
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: lazyEl(InicioPage) },

      // Gestión Académica
      {
        path: 'gestion-academica',
        children: [
          { index: true, element: lazyEl(GestionAcademicaPage) },
          { path: 'programas/:id', element: lazyEl(ProgramaDetallePage) },
          { path: 'programas/:id/unidades-curriculares', element: lazyEl(UnidadesCurricularesPage) },
          { path: 'cohortes/:id', element: lazyEl(CohorteDetallePage) },
          { path: 'cohortes/:id/:tab', element: lazyEl(CohorteDetallePage) },
          { path: 'sesiones/:id/asistencia', element: lazyEl(AsistenciaListaPage) },
        ],
      },

      // Auditoría (top-level)
      { path: 'auditoria', element: lazyEl(AuditoriaPage) },

      // Configuración (layout con sub-rutas)
      {
        path: 'configuracion',
        element: lazyEl(ConfiguracionLayoutPage),
        children: [
          { index: true, element: lazyEl(ConfiguracionGeneralPage) },
          { path: 'usuarios', element: lazyEl(UsuariosPage) },
          { path: 'permisos', element: lazyEl(PermisosPage) },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
